import { Component } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { RoleDirective } from '../../common/directives/role.directive';
import { Role } from '../../common/enums/role.enum';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ProjectService } from '../../services/project.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import {
  IProjectData,
  ProjectsResponse,
} from '../../common/models/project.interface';
import { IClient } from '../../common/models/home-user.interface';
import { SessionContextService } from '../../services/session-context.service';
import { Subscription } from 'rxjs';
import { IUserLocalStorage } from '../../common/models/auth.interface';
import * as QRCode from 'qrcode';

@Component({
  selector: 'app-header',
  imports: [
    RoleDirective,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    FormsModule,
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent {
  Role = Role;
  projects: IProjectData[] = [];
  selected: IProjectData | null = null;
  hideMenuForUrl: boolean = false;
  hideProjectDataForUrl: boolean = false;

  clientsFromUserScreen: IClient | null = null;
  userData: IUserLocalStorage | null = null;
  currentProject: IProjectData | null = null;

  // QR Code properties
  qrCodeDataUrl: string = '';
  showQrCode: boolean = false;

  private subs = new Subscription();

  constructor(
    private authService: AuthService,
    private router: Router,
    private projectService: ProjectService,
    private sessionContextService: SessionContextService,
  ) {}

  ngOnInit(): void {
    this.router.events.subscribe(() => {
      const url = this.router.url.split('?')[0];
      this.hideMenuForUrl = url === '/';
      this.hideProjectDataForUrl = url === '/home/user';
    });

    this.userData = this.authService.getUser() as IUserLocalStorage;

    this.subs.add(
      this.authService.userData$.subscribe((user) => {
        this.userData = user;
        if (this.isAuthenticated()) {
          this.getProjects(user?.clientId as string);
        }
      }),
    );

    this.subs.add(
      this.sessionContextService
        .getClientSessionObservable()
        .subscribe((client) => {
          this.clientsFromUserScreen = client;
        }),
    );

    this.subs.add(
      this.projectService
        .getProjectFromHeaderObservable()
        .subscribe((project) => {
          if (project) {
            this.currentProject = project;
          }
        }),
    );
  }

  getProjects(clientId: string): void {
    this.projectService.getProjectsByClientId(clientId).subscribe({
      next: (res: ProjectsResponse) => {
        this.projects = res.data;

        const storedProjectId =
          this.projectService.getProjectFromLocalStorage()?.id;

        const matchingProject = this.projects.find(
          (p) => p.id === storedProjectId,
        );

        if (matchingProject) {
          this.selected = matchingProject;
        } else {
          this.selected = this.projects[0];
          this.projectService.setCurrentProject(this.selected);
        }
      },
      error: (err) => {
        console.error('Error fetching projects:', err);
      },
    });
  }

  logout(): void {
    this.authService.logout();
  }

  isAuthenticated(): boolean {
    return this.authService.isAuthenticated();
  }

  navigateToLogin(): void {
    this.router.navigate(['/login']);
  }

  navigateToDisclaimer(): void {
    this.router.navigate(['/']);
  }

  navigateToImageBrowser(): void {
    this.router.navigate(['/image-browser']);
  }

  navigateToTraining(): void {
    this.router.navigate(['/home/admin']);
  }

  navigateToProjects(): void {
    this.router.navigate(['/projects']);
  }

  navigateToProducts(): void {
    this.router.navigate(['/products']);
  }

  navigateToClasses(): void {
    this.router.navigate(['/classes']);
  }

  goToClassificationDetection(): void {
    const idProject = this.currentProject?.id;
    const idClient = this.userData?.clientId;

    this.router.navigate(['/home/user'], {
      queryParams: {
        idProject: idProject,
        idClient: idClient
      }
    });
  }

  async generateQRCode(): Promise<void> {
    const idProject = this.currentProject?.id;
    const idClient = this.userData?.clientId;

    // Construir la URL completa
    const baseUrl = window.location.origin;
    const url = `${baseUrl}/home/user?idProject=${idProject}&idClient=${idClient}`;

    try {
      this.qrCodeDataUrl = await QRCode.toDataURL(url, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      this.showQrCode = true;
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  }

  openInNewTab(): void {
    const idProject = this.currentProject?.id;
    const idClient = this.userData?.clientId;

    const baseUrl = window.location.origin;
    const url = `${baseUrl}/home/user?idProject=${idProject}&idClient=${idClient}`;

    window.open(url, '_blank');
  }

  closeQrCode(): void {
    this.showQrCode = false;
    this.qrCodeDataUrl = '';
  }

  onProjectChange(event: MatSelectChange): void {
    const selectedProject = event.value;
    this.projectService.setCurrentProject(selectedProject);
  }
}
